/**
 * Translation Cache Service
 * Caches translations in localStorage to avoid repeated backend requests
 * Especially important for mobile devices
 */

const CACHE_PREFIX = 'crm_translations_';
const CACHE_VERSION_KEY = 'crm_translations_version';
const CACHE_TIMESTAMP_KEY = 'crm_translations_timestamp_';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Get cached translations for a language
 * @param {string} language - Language code (e.g., 'en', 'th')
 * @returns {object|null} Cached translations or null if not found/expired
 */
export const getCachedTranslations = (language) => {
  try {
    const cacheKey = CACHE_PREFIX + language;
    const timestampKey = CACHE_TIMESTAMP_KEY + language;
    
    // Check if cache exists
    const cachedData = localStorage.getItem(cacheKey);
    const timestamp = localStorage.getItem(timestampKey);
    
    if (!cachedData || !timestamp) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`📦 No translation cache: ${language}`);
      }
      return null;
    }
    
    // Check if cache is expired
    const cacheAge = Date.now() - parseInt(timestamp, 10);
    if (cacheAge > CACHE_DURATION) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`⏰ Translation cache expired: ${language}`);
      }
      clearCachedTranslations(language);
      return null;
    }
    
    // Parse and return cached data
    const translations = JSON.parse(cachedData);
    // Only log once per session by checking if we've already logged for this language
    if (process.env.NODE_ENV === 'development' && !window._translationCacheLogged) {
      window._translationCacheLogged = window._translationCacheLogged || {};
      if (!window._translationCacheLogged[language]) {
        console.log(`✅ Translation cache loaded: ${language} (${Object.keys(translations).length} keys)`);
        window._translationCacheLogged[language] = true;
      }
    }
    return translations;
    
  } catch (error) {
    console.error(`❌ [TranslationCache] Error reading cache for ${language}:`, error);
    // Clear corrupted cache
    clearCachedTranslations(language);
    return null;
  }
};

/**
 * Save translations to cache
 * @param {string} language - Language code
 * @param {object} translations - Translation object to cache
 * @returns {boolean} Success status
 */
export const setCachedTranslations = (language, translations) => {
  try {
    if (!translations || typeof translations !== 'object') {
      console.warn(`⚠️ [TranslationCache] Invalid translations object for ${language}`);
      return false;
    }
    
    const cacheKey = CACHE_PREFIX + language;
    const timestampKey = CACHE_TIMESTAMP_KEY + language;
    const translationCount = Object.keys(translations).length;
    
    // Check if translations object is not empty
    if (translationCount === 0) {
      console.warn(`⚠️ [TranslationCache] Empty translations object for ${language}, not caching`);
      return false;
    }
    
    // Store in localStorage
    localStorage.setItem(cacheKey, JSON.stringify(translations));
    localStorage.setItem(timestampKey, Date.now().toString());
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`💾 Cached ${translationCount} translations: ${language}`);
    }
    return true;
    
  } catch (error) {
    console.error(`❌ [TranslationCache] Error saving cache for ${language}:`, error);
    
    // Check if quota exceeded
    if (error.name === 'QuotaExceededError') {
      console.warn(`⚠️ [TranslationCache] localStorage quota exceeded, clearing old caches`);
      clearAllCaches();
      // Try one more time
      try {
        localStorage.setItem(CACHE_PREFIX + language, JSON.stringify(translations));
        localStorage.setItem(CACHE_TIMESTAMP_KEY + language, Date.now().toString());
        return true;
      } catch (retryError) {
        console.error(`❌ [TranslationCache] Still failed after clearing caches`);
        return false;
      }
    }
    
    return false;
  }
};

/**
 * Clear cached translations for a specific language
 * @param {string} language - Language code
 */
export const clearCachedTranslations = (language) => {
  try {
    const cacheKey = CACHE_PREFIX + language;
    const timestampKey = CACHE_TIMESTAMP_KEY + language;
    
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(timestampKey);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`🗑️ Cleared translation cache: ${language}`);
    }
  } catch (error) {
    console.error(`❌ Error clearing translation cache:`, error);
  }
};

/**
 * Clear all cached translations
 */
export const clearAllCaches = () => {
  try {
    const keys = Object.keys(localStorage);
    let cleared = 0;
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX) || key.startsWith(CACHE_TIMESTAMP_KEY)) {
        localStorage.removeItem(key);
        cleared++;
      }
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`🗑️ Cleared ${cleared} translation cache entries`);
    }
  } catch (error) {
    console.error(`❌ [TranslationCache] Error clearing all caches:`, error);
  }
};

/**
 * Get cache info for debugging
 * @returns {object} Cache information
 */
export const getCacheInfo = () => {
  try {
    const info = {
      languages: [],
      totalSize: 0,
      totalKeys: 0
    };
    
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        const language = key.replace(CACHE_PREFIX, '');
        const data = localStorage.getItem(key);
        const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY + language);
        const age = timestamp ? Date.now() - parseInt(timestamp, 10) : null;
        
        if (data) {
          const translations = JSON.parse(data);
          info.languages.push({
            language,
            keyCount: Object.keys(translations).length,
            size: new Blob([data]).size,
            age: age ? Math.round(age / 1000 / 60) : null, // minutes
            expired: age ? age > CACHE_DURATION : false
          });
          info.totalSize += new Blob([data]).size;
          info.totalKeys += Object.keys(translations).length;
        }
      }
    });
    
    return info;
  } catch (error) {
    console.error(`❌ [TranslationCache] Error getting cache info:`, error);
    return { languages: [], totalSize: 0, totalKeys: 0 };
  }
};

/**
 * Force refresh cache (clear and reload)
 * @param {string} language - Language code
 */
export const forceRefreshCache = (language) => {
  clearCachedTranslations(language);
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔄 Force refresh translation cache: ${language}`);
  }
};

/**
 * Check if cache is valid and not expired
 * @param {string} language - Language code
 * @returns {boolean} True if cache is valid
 */
export const isCacheValid = (language) => {
  try {
    const timestampKey = CACHE_TIMESTAMP_KEY + language;
    const timestamp = localStorage.getItem(timestampKey);
    
    if (!timestamp) return false;
    
    const cacheAge = Date.now() - parseInt(timestamp, 10);
    return cacheAge < CACHE_DURATION;
  } catch (error) {
    return false;
  }
};

// Export cache duration for external use
export const TRANSLATION_CACHE_DURATION = CACHE_DURATION;

export default {
  getCachedTranslations,
  setCachedTranslations,
  clearCachedTranslations,
  clearAllCaches,
  getCacheInfo,
  forceRefreshCache,
  isCacheValid,
  CACHE_DURATION
};

